import {
  SearchOutlineIcon,
  SortIcon,
  TableViewIcon,
  ThumbnailsViewIcon,
} from '@carboncopyco/icons';
import type {
  Sort,
  Tag,
  WorkspaceAccess,
  WorkspaceType,
} from '@carboncopyco/types';
import {
  ColorHex,
  DropdownFilter,
  InputText,
  MultiDropdownFilter,
  RadioGroupButton,
} from '@carboncopyco/ui';
import type { ViewMode } from '../../types';
import { useWorkspacesPage } from './WorkspacesPageContext';
import { WorkspacesPageTypeFilterOption } from './WorkspacesPageTypeFilterOption';
import {
  type WorkspaceState,
  accessFilterOptions,
  sortOptions,
  stateOptions,
  typeFilterOptions,
} from './consts';

export const WorkspacesPageControls = () => {
  const {
    setQuery,
    query,
    selectedSort,
    setSelectedSort,
    selectedTypeFilter,
    resetSelectedTypeFilter,
    selectedAccessFilter,
    resetSelectedAccessFilter,
    tagsFilterOptions,
    selectedTagsFilter,
    resetSelectedTagsFilter,
    selectedState,
    setSelectedState,
    setViewMode,
    viewMode,
  } = useWorkspacesPage();

  return (
    <div className="w-full flex justify-center">
      <div className="w-ds-page flex justify-between py-6 items-center">
        <div className="flex gap-3">
          <DropdownFilter<Sort>
            capsuleWidth={130}
            contentWidth={130}
            placeholder=""
            selected={selectedSort}
            leftIcon={SortIcon}
            items={sortOptions}
            onSelect={setSelectedSort}
          />
          <MultiDropdownFilter<WorkspaceAccess>
            capsuleWidth={130}
            contentWidth={130}
            placeholder="All status"
            selectedItems={
              accessFilterOptions
                ? Object.fromEntries(
                    accessFilterOptions
                      ?.filter((access) => selectedAccessFilter.has(access.id))
                      .map((access) => [access.id, access.label])
                  )
                : {}
            }
            fixedGroup={{
              items: accessFilterOptions ?? [],
            }}
            onChange={(value) =>
              selectedAccessFilter.has(value)
                ? selectedAccessFilter.delete(value)
                : selectedAccessFilter.add(value)
            }
            onClear={resetSelectedAccessFilter}
          />
          <MultiDropdownFilter<WorkspaceType>
            capsuleWidth={130}
            contentWidth={130}
            placeholder="All types"
            fixedGroup={{ items: typeFilterOptions ?? [] }}
            renderItem={WorkspacesPageTypeFilterOption}
            selectedItems={
              typeFilterOptions
                ? Object.fromEntries(
                    typeFilterOptions
                      ?.filter((workspaceType) =>
                        selectedTypeFilter.has(workspaceType.id)
                      )
                      .map((workspaceType) => [
                        workspaceType.id,
                        workspaceType.label,
                      ])
                  )
                : {}
            }
            onChange={(selectedType) =>
              selectedTypeFilter.has(selectedType)
                ? selectedTypeFilter.delete(selectedType)
                : selectedTypeFilter.add(selectedType)
            }
            onClear={resetSelectedTypeFilter}
          />
          {tagsFilterOptions?.length > 0 && (
            <MultiDropdownFilter<Tag['id']>
              capsuleWidth={168}
              contentWidth={250}
              placeholder="All tags"
              fixedGroup={{
                items: tagsFilterOptions ?? [],
              }}
              renderItem={WorkspacesPageTypeFilterOption}
              selectedItems={
                tagsFilterOptions
                  ? Object.fromEntries(
                      tagsFilterOptions
                        ?.filter((tag) => selectedTagsFilter.has(tag.id))
                        .map((tag) => [tag.id, tag.label])
                    )
                  : {}
              }
              onChange={(tagId) => {
                selectedTagsFilter.has(tagId)
                  ? selectedTagsFilter.delete(tagId)
                  : selectedTagsFilter.add(tagId);
              }}
              onClear={resetSelectedTagsFilter}
            />
          )}
          <DropdownFilter<WorkspaceState>
            capsuleWidth={130}
            contentWidth={130}
            placeholder=""
            selected={selectedState}
            items={stateOptions}
            onSelect={setSelectedState}
          />
        </div>

        <div className="flex gap-2">
          <div className="w-ds-66">
            <InputText
              placeholder="Search"
              size="lg"
              leftElement={<SearchOutlineIcon color={ColorHex.natural300} />}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
          <RadioGroupButton
            buttonsConfig={[
              {
                id: 'tiles',
                icon: <ThumbnailsViewIcon />,
                tooltip: true,
                tooltipTitle: 'Tile view',
              },
              {
                id: 'table',
                icon: <TableViewIcon />,
                tooltip: true,
                tooltipTitle: 'List view',
              },
            ]}
            onClick={(viewMode: ViewMode) => {
              setViewMode(viewMode);
            }}
            selected={viewMode}
          />
        </div>
      </div>
    </div>
  );
};
